import {
  FilePdfOutlined,
  LockOutlined,
  PlusOutlined,
  SearchOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  ACCOUNT_TYPE_LABELS,
  APPLIES_TO_LABELS,
  COUNTERPARTY_KIND_LABELS,
  describeError,
  dirhams,
  FINANCE_ERRORS,
  fullDate,
  isNegative,
  matchesWords,
  present,
  today,
} from '@horof/shared';
import { Alert, App, Button, Input, Modal, Table, Tag } from 'antd';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery } from 'urql';
import {
  type Account,
  type Category,
  type Counterparty,
  DeleteCategoryDocument,
  DeleteCounterpartyDocument,
  financeContext,
  type Project,
  UpdateProjectDocument,
  useCanWrite,
  useLists,
} from './data';
import {
  AccountDialog,
  accountEdit,
  CategoryDialog,
  type CategoryEdit,
  CounterpartyDialog,
  type CounterpartyEdit,
  ProjectDialog,
  type ProjectEdit,
} from './dialogs';
import { graphql } from './gql';
import { DeskPanel } from './panel';
import { type FitColumn, useFitColumns } from './table-fit';
import {
  type KeyValue,
  KeyValues,
  ListHeader,
  Status,
  TableCard,
  useEditKey,
  ViewBar,
} from './ui';

/**
 * A list of the desk: title, «إضافة», a search, and a table whose rows open
 * read in a drawer (doc 64); «تعديل» opens the edit dialog (treasurer).
 */
function ListScreen<T extends { id: string }>({
  title,
  hint,
  addLabel,
  onAdd,
  rows,
  columns,
  search,
  onOpen,
  loading,
  details,
}: {
  /**
   * A row read in a drawer (doc 64): its title, facts and actions; «تعديل»
   * opens `onOpen`'s dialog.
   */
  details: (row: T) => {
    title: string;
    items: (KeyValue | false | null | undefined)[];
    actions?: ReactNode;
  };
  title: string;
  hint?: ReactNode;
  addLabel: string;
  onAdd: () => void;
  rows: T[];
  columns: FitColumn<T>[];
  /** The text a row is searched in. */
  search: (row: T) => (string | null | undefined)[];
  onOpen: (row: T) => void;
  loading: boolean;
}) {
  const { ref: fitRef, fit } = useFitColumns();
  const canWrite = useCanWrite();
  // `?q=` (from the header's search) opens the list filtered.
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  useEffect(() => {
    const q = params.get('q');
    if (q !== null) setQuery(q);
  }, [params]);
  const shown = rows.filter((r) => matchesWords(search(r), query));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = rows.find((r) => r.id === selectedId) ?? null;
  const read = selected ? details(selected) : null;
  // E opens the dialog, as in the other drawers (doc 61).
  useEditKey(!!selected && canWrite, () => selected && onOpen(selected));
  return (
    <section className="desk-page" ref={fitRef}>
      <ListHeader
        title={title}
        count={loading ? undefined : rows.length}
        description={hint}
        actions={
          canWrite && (
            <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
              {addLabel}
            </Button>
          )
        }
      />
      <TableCard
        toolbar={
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="بحث"
            className="desk-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      >
        <Table<T>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          columns={fit(columns)}
          dataSource={shown}
          loading={loading}
          pagination={false}
          scroll={{ y: 'calc(100vh - 280px)' }}
          onRow={(row) => ({
            onClick: () => setSelectedId(row.id),
            className:
              row.id === selectedId ? 'is-open is-clickable' : 'is-clickable',
          })}
          locale={{ emptyText: query ? 'لا نتيجة' : 'القائمة فارغة' }}
        />
      </TableCard>
      {selected && read && (
        <DeskPanel
          key={selected.id}
          title={read.title}
          recordId={selected.id}
          onClose={() => setSelectedId(null)}
        >
          <div className="ui-stack">
            <KeyValues columns={2} items={read.items} />
            <ViewBar onEdit={canWrite ? () => onOpen(selected) : undefined}>
              {read.actions}
            </ViewBar>
          </div>
        </DeskPanel>
      )}
    </section>
  );
}

/** Buttons in a row: a click there doesn't open the row's dialog. */
function RowActions({ children }: { children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only stops the row's click
    // biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside handle keys
    <div className="desk-row-actions" onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  );
}

const operationsLink = (key: string, id: string) => (
  <Link to={`/operations?${key}=${id}`}>العمليات</Link>
);

const ProjectTotalsDocument = graphql(`
  query DeskProjectTotals {
    financeTotals(groupBy: "project") {
      nodes {
        key
        income
        expenses
        result
      }
    }
  }
`);

export function ProjectsScreen() {
  const canWrite = useCanWrite();
  const { modal, message } = App.useApp();
  const [{ data, fetching }] = useLists();
  // All-time totals by project (arqam doc 55: without transfers and loans).
  const [{ data: totals }] = useQuery({
    query: ProjectTotalsDocument,
    context: useMemo(() => financeContext, []),
  });
  const totalOf = new Map(
    present(totals?.financeTotals?.nodes).map((t) => [t.key, t]),
  );
  const [editing, setEditing] = useState<ProjectEdit | null>(null);
  const [closing, setClosing] = useState<Project | null>(null);
  const [, update] = useMutation(UpdateProjectDocument);
  const rows = present(data?.projects?.nodes).sort(
    (a, b) => Number(a.status === 'CLOSED') - Number(b.status === 'CLOSED'),
  );

  async function reopen(p: Project) {
    const ok = await modal.confirm({
      title: `إعادة فتح «${p.name}»`,
      content:
        'يمكن بعدها إضافة عمليات إليه وتعديل عملياته من جديد. يُحذف تاريخ الإغلاق وسببه.',
      okText: 'أعد فتحه',
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await update(
      { id: p.id, patch: { status: 'OPEN' } },
      financeContext,
    );
    if (r.error) {
      message.error(
        describeError(r.error, 'تعذّر فتح المشروع.', FINANCE_ERRORS),
      );
    } else {
      message.success('أُعيد فتح المشروع');
    }
  }

  const actionsOf = (p: Project) => (
    <>
      {operationsLink('project', p.id)}
      <a href={p.reportLink} target="_blank" rel="noreferrer">
        <FilePdfOutlined /> التقرير
      </a>
      {canWrite &&
        (p.status === 'CLOSED' ? (
          <Button
            size="small"
            icon={<UnlockOutlined />}
            onClick={() => reopen(p)}
          >
            إعادة الفتح
          </Button>
        ) : (
          <Button
            size="small"
            icon={<LockOutlined />}
            onClick={() => setClosing(p)}
          >
            إغلاق
          </Button>
        ))}
    </>
  );

  return (
    <>
      <ListScreen<Project>
        title="المشاريع"
        hint="كل عملية تنتمي إلى مشروع. المشروع المغلق تُجمَّد عملياته."
        addLabel="مشروع جديد"
        onAdd={() => setEditing({ id: null, name: '', description: '' })}
        rows={rows}
        loading={fetching && !data}
        search={(p) => [p.name, p.description]}
        onOpen={(p) =>
          setEditing({
            id: p.id,
            name: p.name,
            description: p.description ?? '',
          })
        }
        details={(p) => {
          const t = totalOf.get(p.id);
          return {
            title: p.name,
            items: [
              {
                label: 'الحالة',
                value:
                  p.status === 'CLOSED' ? (
                    <Status tone="stopped">
                      مغلق{p.closedOn ? ` منذ ${fullDate(p.closedOn)}` : ''}
                    </Status>
                  ) : (
                    <Status tone="success">مفتوح</Status>
                  ),
              },
              p.status === 'CLOSED' && {
                label: 'سبب الإغلاق',
                value: p.closedReason,
              },
              { label: 'الوصف', value: p.description, wide: true },
              t && {
                label: 'المداخيل',
                value: (
                  <span className="desk-amount is-credit">
                    {dirhams(t.income)}
                  </span>
                ),
              },
              t && {
                label: 'المصاريف',
                value: (
                  <span className="desk-amount is-debit">
                    {dirhams(t.expenses)}
                  </span>
                ),
              },
              t && {
                label: 'النتيجة',
                value: <strong>{dirhams(t.result)}</strong>,
              },
            ],
            actions: actionsOf(p),
          };
        }}
        columns={[
          { key: 'name', title: 'المشروع', dataIndex: 'name', ellipsis: true },
          {
            key: 'totals',
            drop: 1,
            title: 'المداخيل · المصاريف · النتيجة',
            width: 330,
            render: (_, p) => {
              const t = totalOf.get(p.id);
              if (!t) return <span className="field-hint">—</span>;
              return (
                <span className="desk-project-totals">
                  <span className="desk-amount is-credit">
                    {dirhams(t.income)}
                  </span>
                  <span className="desk-amount is-debit">
                    {dirhams(t.expenses)}
                  </span>
                  <strong
                    className={
                      isNegative(t.result) ? 'desk-amount is-debit' : ''
                    }
                  >
                    {dirhams(t.result)}
                  </strong>
                </span>
              );
            },
          },
          {
            key: 'status',
            drop: 2,
            title: 'الحالة',
            width: 200,
            render: (_, p) =>
              p.status === 'CLOSED' ? (
                <span title={p.closedReason ?? undefined}>
                  <Tag icon={<LockOutlined />}>
                    مغلق{p.closedOn ? ` منذ ${fullDate(p.closedOn)}` : ''}
                  </Tag>
                </span>
              ) : (
                <Tag color="green">مفتوح</Tag>
              ),
          },
          {
            key: 'actions',
            title: '',
            width: 300,
            render: (_, p) => <RowActions>{actionsOf(p)}</RowActions>,
          },
        ]}
      />
      {editing && (
        <ProjectDialog project={editing} onClose={() => setEditing(null)} />
      )}
      {closing && (
        <CloseProjectDialog
          project={closing}
          onClose={() => setClosing(null)}
        />
      )}
    </>
  );
}

/** Closing freezes the project's operations (arqam doc 50). */
function CloseProjectDialog({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [closedOn, setClosedOn] = useState(today());
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching }, update] = useMutation(UpdateProjectDocument);

  async function save() {
    const r = await update(
      {
        id: project.id,
        patch: {
          status: 'CLOSED',
          closedOn: closedOn || null,
          closedReason: reason.trim() || null,
        },
      },
      financeContext,
    );
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر إغلاق المشروع.', FINANCE_ERRORS));
      return;
    }
    message.success(`أُغلق المشروع «${project.name}»`);
    onClose();
  }

  return (
    <Modal
      open
      title={`إغلاق المشروع: ${project.name}`}
      okText="أغلق المشروع"
      okButtonProps={{ danger: true }}
      cancelText="إلغاء"
      confirmLoading={fetching}
      onOk={save}
      onCancel={onClose}
    >
      <div className="desk-dialog">
        <Alert
          type="warning"
          showIcon
          message="ستُجمَّد عملياته"
          description="لا يمكن بعد الإغلاق إضافة عمليات إليه، ولا تعديل عملياته أو حذفها أو نقلها، حتى يُعاد فتحه."
        />
        <div className="desk-dialog-row">
          <div className="desk-dialog-field">
            <label htmlFor="closed-on">تاريخ الإغلاق</label>
            <Input
              id="closed-on"
              type="date"
              className="ltr-num"
              value={closedOn}
              onChange={(e) => setClosedOn(e.target.value)}
            />
          </div>
          <div className="desk-dialog-field">
            <label htmlFor="closed-reason">السبب (اختياري)</label>
            <Input
              id="closed-reason"
              placeholder="مثلاً: انتهت الأشغال"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}

export function AccountsScreen() {
  const [{ data, fetching }] = useLists();
  const [editing, setEditing] = useState<ReturnType<typeof accountEdit> | null>(
    null,
  );
  const actionsOf = (a: Account) => <>{operationsLink('account', a.id)}</>;

  return (
    <>
      <ListScreen<Account>
        title="الحسابات"
        hint="الرصيد يحسبه الخادم: رصيد الافتتاح + العمليات بعد تاريخه. الرصيد السالب يعني أن عمليات تنقص من السجل."
        addLabel="حساب جديد"
        onAdd={() =>
          setEditing({
            id: null,
            name: '',
            type: 'cash',
            accountNumber: '',
            openingBalance: '',
            openingDate: '',
          })
        }
        rows={present(data?.financialAccounts?.nodes)}
        loading={fetching && !data}
        search={(a) => [a.name, a.accountNumber, ACCOUNT_TYPE_LABELS[a.type]]}
        onOpen={(a) => setEditing(accountEdit(a))}
        details={(a) => ({
          title: a.name,
          items: [
            { label: 'النوع', value: ACCOUNT_TYPE_LABELS[a.type] },
            {
              label: 'الرقم',
              value: a.accountNumber && (
                <span className="ltr-num">{a.accountNumber}</span>
              ),
            },
            {
              label: 'الرصيد الحالي',
              value: (
                <strong
                  className={`desk-amount${isNegative(a.currentBalance) ? ' is-debit' : ''}`}
                >
                  {dirhams(a.currentBalance)}
                </strong>
              ),
            },
            {
              label: 'رصيد الافتتاح',
              value: a.openingDate
                ? `${dirhams(a.openingBalance)} · ${fullDate(a.openingDate)}`
                : null,
            },
          ],
          actions: actionsOf(a),
        })}
        columns={[
          { key: 'name', title: 'الحساب', dataIndex: 'name', ellipsis: true },
          {
            key: 'type',
            title: 'النوع',
            width: 140,
            render: (_, a) => ACCOUNT_TYPE_LABELS[a.type],
          },
          {
            key: 'number',
            drop: 1,
            title: 'الرقم',
            width: 230,
            render: (_, a) => (
              <span className="ltr-num">{a.accountNumber}</span>
            ),
          },
          {
            key: 'opening',
            drop: 2,
            title: 'رصيد الافتتاح',
            width: 220,
            render: (_, a) =>
              a.openingDate ? (
                <span>
                  {dirhams(a.openingBalance)}{' '}
                  <span className="field-hint">{a.openingDate}</span>
                </span>
              ) : (
                <span className="field-hint">—</span>
              ),
          },
          {
            key: 'balance',
            title: 'الرصيد الحالي',
            width: 170,
            render: (_, a) => (
              <strong
                className={`desk-amount${isNegative(a.currentBalance) ? ' is-debit' : ''}`}
              >
                {dirhams(a.currentBalance)}
              </strong>
            ),
          },
          {
            key: 'actions',
            title: '',
            width: 100,
            render: (_, a) => <RowActions>{actionsOf(a)}</RowActions>,
          },
        ]}
      />
      {editing && (
        <AccountDialog account={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

/** Deleting what operations still use fails: say so instead of the code. */
function useDelete() {
  const { modal, message } = App.useApp();
  return async (
    what: string,
    name: string,
    run: () => Promise<{ error?: Parameters<typeof describeError>[0] }>,
    inUse: string,
  ) => {
    const ok = await modal.confirm({
      title: `حذف ${what}`,
      content: `«${name}». لا يمكن التراجع.`,
      okText: 'احذف',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await run();
    if (r.error) {
      message.error(
        describeError(r.error, 'تعذّر الحذف.', {
          ...FINANCE_ERRORS,
          FOREIGN_KEY_VIOLATION: inUse,
        }),
        6,
      );
    } else {
      message.success(`حُذف: ${name}`);
    }
  };
}

export function CategoriesScreen() {
  const canWrite = useCanWrite();
  const [{ data, fetching }] = useLists();
  const [editing, setEditing] = useState<CategoryEdit | null>(null);
  const [, remove] = useMutation(DeleteCategoryDocument);
  const del = useDelete();
  const actionsOf = (c: Category) => (
    <>
      {operationsLink('category', c.id)}
      {canWrite && (
        <Button
          size="small"
          danger
          type="text"
          onClick={() =>
            del(
              'الصنف',
              c.name,
              () => remove({ id: c.id }, financeContext),
              'لا يمكن حذف صنف مستعمل في عمليات. غيّر اسمه، أو انقل عملياته إلى صنف آخر أولاً.',
            )
          }
        >
          حذف
        </Button>
      )}
    </>
  );

  return (
    <>
      <ListScreen<Category>
        title="الأصناف"
        hint="ما هي العملية: كهرباء، أجور، فواتير الماء… كل صنف للمداخيل أو للمصاريف أو لكليهما."
        addLabel="صنف جديد"
        onAdd={() => setEditing({ id: null, name: '', appliesTo: 'debit' })}
        rows={present(data?.operationCategories?.nodes)}
        loading={fetching && !data}
        search={(c) => [c.name, APPLIES_TO_LABELS[c.appliesTo]]}
        onOpen={(c) =>
          setEditing({ id: c.id, name: c.name, appliesTo: c.appliesTo })
        }
        details={(c) => ({
          title: c.name,
          items: [
            { label: 'يُستعمل في', value: APPLIES_TO_LABELS[c.appliesTo] },
          ],
          actions: actionsOf(c),
        })}
        columns={[
          { key: 'name', title: 'الصنف', dataIndex: 'name', ellipsis: true },
          {
            key: 'appliesTo',
            drop: 1,
            title: 'يُستعمل في',
            width: 160,
            render: (_, c) => (
              <Tag
                color={
                  c.appliesTo === 'credit'
                    ? 'green'
                    : c.appliesTo === 'debit'
                      ? 'red'
                      : 'default'
                }
              >
                {APPLIES_TO_LABELS[c.appliesTo]}
              </Tag>
            ),
          },
          {
            key: 'actions',
            title: '',
            width: 200,
            render: (_, c) => <RowActions>{actionsOf(c)}</RowActions>,
          },
        ]}
      />
      {editing && (
        <CategoryDialog category={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

export function CounterpartiesScreen() {
  const canWrite = useCanWrite();
  const [{ data, fetching }] = useLists();
  const [editing, setEditing] = useState<CounterpartyEdit | null>(null);
  const [, remove] = useMutation(DeleteCounterpartyDocument);
  const del = useDelete();
  const actionsOf = (c: Counterparty) => (
    <>
      {operationsLink('counterparty', c.id)}
      {canWrite && (
        <Button
          size="small"
          danger
          type="text"
          onClick={() =>
            del(
              'الجهة',
              c.name,
              () => remove({ id: c.id }, financeContext),
              'لا يمكن حذف جهة مرتبطة بعمليات أو ديون أو مساهمات.',
            )
          }
        >
          حذف
        </Button>
      )}
    </>
  );

  return (
    <>
      <ListScreen<Counterparty>
        title="الجهات"
        hint="من تُدفع له الأموال أو منه: الموردون، العمال، المتبرعون، المُقرضون…"
        addLabel="جهة جديدة"
        onAdd={() =>
          setEditing({
            id: null,
            name: '',
            kind: 'supplier',
            phone: '',
            note: '',
          })
        }
        rows={present(data?.counterparties?.nodes)}
        loading={fetching && !data}
        search={(c) => [
          c.name,
          COUNTERPARTY_KIND_LABELS[c.kind],
          c.phone,
          c.note,
        ]}
        onOpen={(c) =>
          setEditing({
            id: c.id,
            name: c.name,
            kind: c.kind,
            phone: c.phone ?? '',
            note: c.note ?? '',
          })
        }
        details={(c) => ({
          title: c.name,
          items: [
            { label: 'النوع', value: COUNTERPARTY_KIND_LABELS[c.kind] },
            {
              label: 'الهاتف',
              value: c.phone && <span className="ltr-num">{c.phone}</span>,
            },
            { label: 'ملاحظة', value: c.note, wide: true },
          ],
          actions: actionsOf(c),
        })}
        columns={[
          { key: 'name', title: 'الجهة', dataIndex: 'name', ellipsis: true },
          {
            key: 'kind',
            drop: 2,
            title: 'النوع',
            width: 120,
            render: (_, c) => <Tag>{COUNTERPARTY_KIND_LABELS[c.kind]}</Tag>,
          },
          {
            key: 'phone',
            drop: 1,
            title: 'الهاتف',
            width: 150,
            render: (_, c) => <span className="ltr-num">{c.phone}</span>,
          },
          { key: 'note', title: 'ملاحظة', dataIndex: 'note', ellipsis: true },
          {
            key: 'actions',
            title: '',
            width: 200,
            render: (_, c) => <RowActions>{actionsOf(c)}</RowActions>,
          },
        ]}
      />
      {editing && (
        <CounterpartyDialog
          counterparty={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
